import type { DeliveryType } from "@core/schema/DeliveryType.js";
import type { Locale } from "@core/schema/Locale.js";
import type { Order } from "@core/schema/Order.js";
import type { OrderProduct } from "@core/schema/OrderProduct.js";
import type { PaymentType } from "@core/schema/PaymentType.js";
import type { Product } from "@core/schema/Product.js";
import { deliveryTypeNameMap } from "@core/schema/names/deliveryTypeNameMap.js";
import { paymentTypeNameMap } from "@core/schema/names/paymentTypeNameMap.js";
import { getPriceOfOrder } from "@core/schema/utils/getPriceOfOrder.js";
import { getPriceOfOrderProduct } from "@core/schema/utils/getPriceOfOrderProduct.js";
import { transformProductToOrderProduct } from "@core/schema/utils/transformProductToOrderProduct.js";
import { roundToPlaces } from "@core/utils/roundToPlaces.js";
import type { OrderAfterOverview, OrderInOverview } from "./cart/Cart.js";

declare const gtag: (...args: unknown[]) => void;

interface DynamicRemarketingEvent {
	event: string;
	ecommerce?: Record<string, unknown>;
	[other: string]: unknown;
}

interface Item {
	item_name: string;
	item_id: string;
	item_variant?: string;
	price: number;
	quantity: number;
}

function getProductItem(orderProduct: OrderProduct): Item {
	const price = getPriceOfOrderProduct({ ...orderProduct, quantity: 1 });
	return {
		item_name: orderProduct.name,
		item_id: orderProduct.productId,
		price: roundToPlaces(price.withoutTax, 2),
		quantity: orderProduct.quantity,
	};
}

export function reportProductDetail(product: Product): void {
	const orderProduct = transformProductToOrderProduct(crypto.randomUUID(), product, 1);
	const price = getPriceOfOrderProduct(orderProduct);

	const event: DynamicRemarketingEvent = {
		event: "view_item",
		ecommerce: {
			currency: "CZK",
			value: roundToPlaces(price.withoutTax, 2),
			items: [getProductItem(orderProduct)],
		},
	};

	gtag("event", event.event, event.ecommerce);
}

export function reportAddedProductToCart(product: OrderProduct, itemQuantity?: number): void {
	const productOfQuantity = { ...product, quantity: itemQuantity ?? product.quantity };
	const price = getPriceOfOrderProduct(productOfQuantity);

	const event: DynamicRemarketingEvent = {
		event: "add_to_cart",
		ecommerce: {
			currency: "CZK",
			value: roundToPlaces(price.withoutTax, 2),
			items: [getProductItem(productOfQuantity)],
		},
	};

	gtag("event", event.event, event.ecommerce);
}

export function reportRemovedProductFromCart(product: OrderProduct, itemQuantity?: number): void {
	const quantity = itemQuantity ?? product.quantity;
	const productOfQuantity = { ...product, quantity };
	const price = getPriceOfOrderProduct(productOfQuantity);
	const event: DynamicRemarketingEvent = {
		event: "remove_from_cart",
		ecommerce: {
			currency: "CZK",
			value: roundToPlaces(price.withoutTax, 2),
			items: [getProductItem(productOfQuantity)],
		},
	};

	gtag("event", event.event, event.ecommerce);
}

export function reportCheckoutBegan(order: OrderInOverview): void {
	const price = getPriceOfOrder(order);
	const event: DynamicRemarketingEvent = {
		event: "begin_checkout",
		ecommerce: {
			currency: "CZK",
			value: roundToPlaces(price.totalPrice.withoutTax, 2),
			items: [...Object.values(order.products).map((item) => getProductItem(item))],
		},
	};

	gtag("event", event.event, event.ecommerce);
}

export function onPaymentTypeChanged(order: OrderAfterOverview, locale: Locale, paymentType: PaymentType): void {
	const price = getPriceOfOrder(order);
	const event: DynamicRemarketingEvent = {
		event: "add_payment_info",
		ecommerce: {
			currency: "CZK",
			value: roundToPlaces(price.totalPrice.withoutTax, 2),
			payment_type: paymentTypeNameMap[paymentType][locale],
			items: [...Object.values(order.products).map((item) => getProductItem(item))],
		},
	};

	gtag("event", event.event, event.ecommerce);
}

export function onDeliveryTypeChanged(order: OrderAfterOverview, locale: Locale, deliveryType: DeliveryType): void {
	const price = getPriceOfOrder(order);
	const event: DynamicRemarketingEvent = {
		event: "add_shipping_info",
		ecommerce: {
			currency: "CZK",
			value: roundToPlaces(price.totalPrice.withoutTax, 2),
			shipping_tier: deliveryTypeNameMap[deliveryType][locale],
			items: [...Object.values(order.products).map((item) => getProductItem(item))],
		},
	};

	gtag("event", event.event, event.ecommerce);
}

export function reportPurchase(order: Order): void {
	const priceWithTax = order.prices.totalPrice.withTax;
	const priceWithoutTax = order.prices.totalPrice.withoutTax;
	const event: DynamicRemarketingEvent = {
		event: "purchase",
		ecommerce: {
			currency: "CZK",
			value: roundToPlaces(priceWithoutTax, 2),
			tax: roundToPlaces(priceWithTax - priceWithoutTax, 2),
			shipping: roundToPlaces(order.prices.deliveryPrice, 2),
			transaction_id: order.goPayData?.id ?? "",
			items: [...Object.values(order.products).map((item) => getProductItem(item))],
		},
	};

	gtag("event", event.event, event.ecommerce);
}
